import { initFirebase } from '../firebase'
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore'

const { db } = initFirebase()

// AI cost / token usage — read directly from Firestore via the client SDK.
//
// The deployed `ryomen` rules are `allow read, write: if request.auth != null`, and the
// app signs in anonymously on startup (FirebaseAuthService), so these collections are
// readable from the browser just like users/transcriptions/compatibility. No backend
// endpoint needed.
//
// Queries use single-field indexes only (auto-created), so no composite index setup:
//   aiUsageDaily : where date in [start,end] orderBy date
//   aiUsageByUser: orderBy totalCostUSD desc limit N
//   aiUsage      : where userId == X  (dateKey range filtered client-side)

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function normByFn(byFn) {
  const out = {}
  if (byFn && typeof byFn === 'object') {
    Object.keys(byFn).forEach(k => {
      const e = byFn[k] || {}
      out[k] = { calls: num(e.calls), totalTokens: num(e.totalTokens), costUSD: num(e.costUSD) }
    })
  }
  return out
}

// byModel: map of modelKey -> { calls, totalTokens, costUSD, provider }.
// Keys had dots replaced with underscores (Firestore field constraint), e.g.
// gpt-4.1 -> gpt-4_1 — the UI converts them back for display.
function normByModel(byModel) {
  const out = {}
  if (byModel && typeof byModel === 'object') {
    Object.keys(byModel).forEach(k => {
      const e = byModel[k] || {}
      out[k] = {
        calls: num(e.calls),
        totalTokens: num(e.totalTokens),
        costUSD: num(e.costUSD),
        provider: e.provider || ''
      }
    })
  }
  return out
}

export class FirebaseAiUsageService {
  async getUsage({ startDate, endDate, topUsers = 20, userId = null }) {
    if (userId) {
      return { daily: [], topUsers: [], userDrilldown: await this._drilldown(userId, startDate, endDate) }
    }
    const [daily, top] = await Promise.all([
      this._daily(startDate, endDate),
      this._topUsers(topUsers)
    ])
    return { daily, topUsers: top, userDrilldown: null }
  }

  async _daily(startDate, endDate) {
    const q = query(
      collection(db, 'aiUsageDaily'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date')
    )
    const snap = await getDocs(q)
    return snap.docs.map(d => {
      const x = d.data()
      return {
        date: x.date || d.id,
        calls: num(x.calls),
        promptTokens: num(x.promptTokens),
        completionTokens: num(x.completionTokens),
        cachedTokens: num(x.cachedTokens),
        totalTokens: num(x.totalTokens),
        costUSD: num(x.costUSD),
        byFn: normByFn(x.byFn),
        byModel: normByModel(x.byModel)
      }
    })
  }

  async _topUsers(n) {
    const q = query(collection(db, 'aiUsageByUser'), orderBy('totalCostUSD', 'desc'), limit(n))
    const snap = await getDocs(q)
    return snap.docs.map(d => {
      const x = d.data()
      return {
        userId: x.userId || d.id,
        calls: num(x.calls),
        promptTokens: num(x.promptTokens),
        completionTokens: num(x.completionTokens),
        cachedTokens: num(x.cachedTokens),
        totalTokens: num(x.totalTokens),
        totalCostUSD: num(x.totalCostUSD),
        byFn: normByFn(x.byFn),
        byModel: normByModel(x.byModel),
        lastUsedAt: x.lastUsedAt || null
      }
    })
  }

  async _drilldown(userId, startDate, endDate) {
    const q = query(collection(db, 'aiUsage'), where('userId', '==', userId), limit(1000))
    const snap = await getDocs(q)
    return snap.docs
      .map(d => {
        const x = d.data()
        return {
          id: d.id,
          fn: x.fn || '',
          provider: x.provider || '',
          model: x.model || '',
          promptTokens: num(x.promptTokens),
          completionTokens: num(x.completionTokens),
          cachedTokens: num(x.cachedTokens),
          totalTokens: num(x.totalTokens),
          costUSD: num(x.costUSD),
          userId: x.userId || userId,
          dateKey: x.dateKey || '',
          createdAt: x.createdAt || null
        }
      })
      .filter(r => (!startDate || !r.dateKey || r.dateKey >= startDate) && (!endDate || !r.dateKey || r.dateKey <= endDate))
  }
}
